import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getExpenseUploads, uploadExpenseOcr, getExpenseUpload,
  reprocessExpenseUpload, confirmExpenseUpload, deleteExpenseUpload,
  getProviders,
} from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import PageHelpButton from '../components/help/PageHelpButton';
import {
  Upload, Camera, Loader2, RefreshCw, Check, X, AlertTriangle,
  FileText, Trash2, Eye, ChevronRight, Zap, Inbox, CheckCircle2, XCircle, Clock,
  FileWarning
} from 'lucide-react';

const CATEGORY_LABELS = {
  proveedores: 'Proveedores',
  combustible: 'Combustible',
  comidas: 'Comidas y Dietas',
  material_oficina: 'Material de Oficina',
  alquiler: 'Alquiler',
  seguros: 'Seguros',
  transporte: 'Transporte',
  software: 'Software',
  telefonia: 'Telefonía',
  formacion: 'Formación',
  suministros: 'Suministros',
  vehiculo: 'Vehículo',
  otros: 'Otros',
};

const STATUS_META = {
  uploaded:   { label: 'En cola',     color: 'bg-stone-100 text-stone-600',   icon: Clock },
  processing: { label: 'Procesando',  color: 'bg-blue-100 text-blue-700',     icon: Loader2 },
  extracted:  { label: 'Por revisar', color: 'bg-amber-100 text-amber-700',   icon: Eye },
  confirmed:  { label: 'Confirmado',  color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  failed:     { label: 'Falló',       color: 'bg-rose-100 text-rose-700',     icon: XCircle },
};

const STATUS_TABS = [
  { key: 'all',       label: 'Todos' },
  { key: 'extracted', label: 'Por revisar' },
  { key: 'processing',label: 'Procesando' },
  { key: 'failed',    label: 'Errores' },
  { key: 'confirmed', label: 'Confirmados' },
];

const formatCurrency = (n) =>
  new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(n || 0);

// Render confidence ring: green > 0.85, amber > 0.6, rose otherwise
const confidenceColor = (score) => {
  if (score == null) return null;
  if (score >= 0.85) return 'border-emerald-300 bg-emerald-50';
  if (score >= 0.6) return 'border-amber-300 bg-amber-50';
  return 'border-rose-300 bg-rose-50';
};
const confidenceLabel = (score) => {
  if (score == null) return null;
  if (score >= 0.85) return { tag: 'Alta', cls: 'text-emerald-700 bg-emerald-100' };
  if (score >= 0.6)  return { tag: 'Media', cls: 'text-amber-700 bg-amber-100' };
  return { tag: 'Baja', cls: 'text-rose-700 bg-rose-100' };
};

const ExpenseInboxPage = () => {
  const navigate = useNavigate();
  const [uploads, setUploads] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('extracted');
  const [providers, setProviders] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmForm, setConfirmForm] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const params = activeTab !== 'all' ? { status: activeTab } : {};
      const [up, prv] = await Promise.all([
        getExpenseUploads(params),
        getProviders().catch(() => ({ data: [] })),
      ]);
      setUploads(up.data?.items || []);
      setCounts(up.data?.counts || {});
      setProviders(prv.data || []);
    } catch (e) {
      toast.error('Error cargando bandeja OCR');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-poll while there are items in processing/uploaded
  useEffect(() => {
    const hasInFlight = uploads.some((u) => ['uploaded', 'processing'].includes(u.status));
    if (hasInFlight && !pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(fetchData, 3500);
    }
    if (!hasInFlight && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [uploads, fetchData]);

  const handleFiles = async (fileList, source = 'web') => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setUploading(true);
    try {
      const res = await uploadExpenseOcr(files, source);
      toast.success(`${res.data?.count || files.length} archivo(s) en cola para OCR`);
      setActiveTab('extracted');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error subiendo archivos');
    } finally {
      setUploading(false);
    }
  };

  const onFileInputChange = (e) => {
    handleFiles(e.target.files, 'web');
    e.target.value = '';
  };

  const onCameraInputChange = (e) => {
    handleFiles(e.target.files, 'mobile');
    e.target.value = '';
  };

  const onDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); setDragActive(false); };
  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files, 'web');
  };

  const openDetail = async (upload) => {
    try {
      const res = await getExpenseUpload(upload.id);
      const u = res.data;
      setSelected(u);
      const d = u.extracted_data || {};
      const conf = u.confidence_scores || {};
      const matchedProvId = u.provider_match?.provider_id || '';
      setConfirmForm({
        vendor: d.supplier_name || '',
        vendor_tax_id: d.supplier_tax_id || '',
        category: d.suggested_category || 'otros',
        description: d.invoice_number || '',
        amount: d.subtotal ?? '',
        tax_amount: d.tax_amount ?? '',
        tax_rate: 21,
        total: d.total ?? '',
        expense_date: d.issue_date || '',
        notes: d.notes || '',
        provider_id: matchedProvId,
        create_provider: !matchedProvId && !!d.supplier_name,
        is_partial_deductible: !!d.is_partial_deductible,
        make_recurring: false,
        recurring_frequency: 'monthly',
        _conf: conf,
        _line_items: d.line_items || [],
        _duplicate: u.duplicate_warning,
        _match: u.provider_match,
      });
    } catch (e) {
      toast.error('No se pudo cargar el detalle');
    }
  };

  const closeDetail = () => { setSelected(null); setConfirmForm(null); };

  const handleConfirm = async () => {
    if (!selected) return;
    if (!confirmForm.vendor || !confirmForm.total) {
      toast.error('Proveedor y total son obligatorios');
      return;
    }
    try {
      const payload = { ...confirmForm };
      delete payload._conf; delete payload._line_items; delete payload._duplicate; delete payload._match;
      payload.amount = parseFloat(payload.amount) || 0;
      payload.tax_amount = parseFloat(payload.tax_amount) || 0;
      payload.tax_rate = parseFloat(payload.tax_rate) || 21;
      payload.total = parseFloat(payload.total) || 0;
      if (!payload.provider_id) delete payload.provider_id;
      await confirmExpenseUpload(selected.id, payload);
      toast.success('Gasto confirmado y creado');
      closeDetail();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al confirmar');
    }
  };

  const handleReprocess = async (id) => {
    try {
      await reprocessExpenseUpload(id);
      toast.success('Reprocesando…');
      fetchData();
    } catch (e) {
      toast.error('Error al reprocesar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Descartar este archivo? No se puede deshacer.')) return;
    try {
      await deleteExpenseUpload(id);
      toast.success('Archivo descartado');
      if (selected?.id === id) closeDetail();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const calcTotals = (field, value) => {
    setConfirmForm((prev) => {
      const data = { ...prev, [field]: value };
      const rate = parseFloat(data.tax_rate) || 0;
      if (field === 'total') {
        const total = parseFloat(value) || 0;
        const base = total / (1 + rate / 100);
        data.amount = base ? base.toFixed(2) : '';
        data.tax_amount = base ? (total - base).toFixed(2) : '';
      } else if (field === 'amount') {
        const base = parseFloat(value) || 0;
        data.tax_amount = (base * rate / 100).toFixed(2);
        data.total = (base + parseFloat(data.tax_amount || 0)).toFixed(2);
      } else if (field === 'tax_rate') {
        const base = parseFloat(data.amount) || 0;
        data.tax_amount = (base * (parseFloat(value) || 0) / 100).toFixed(2);
        data.total = (base + parseFloat(data.tax_amount || 0)).toFixed(2);
      }
      return data;
    });
  };

  const InboxBadge = ({ label, count, color }) => (
    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${color}`}>{label}: {count || 0}</span>
  );

  return (
    <div className="space-y-6" data-testid="expense-inbox-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-stone-900 flex items-center gap-3">
              <Inbox className="w-8 h-8 text-[#FF6B4A]" />
              Bandeja OCR
            </h1>
            <p className="text-sm text-stone-500 mt-1">Sube tus tickets y facturas. La IA extraerá los datos y los dejará listos para revisar.</p>
          </div>
          <PageHelpButton moduleKey="expenses-inbox" variant="icon" />
        </div>
        <div className="flex flex-wrap gap-2">
          <InboxBadge label="Por revisar" count={counts.extracted} color="bg-amber-100 text-amber-700" />
          <InboxBadge label="Procesando" count={counts.processing} color="bg-blue-100 text-blue-700" />
          <InboxBadge label="Errores" count={counts.failed} color="bg-rose-100 text-rose-700" />
          <InboxBadge label="Confirmados" count={counts.confirmed} color="bg-emerald-100 text-emerald-700" />
        </div>
      </div>

      {/* Upload zone */}
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
          dragActive ? 'border-[#FF6B4A] bg-orange-50 scale-[1.01]' : 'border-stone-200 bg-white'
        }`}
        data-testid="upload-zone"
      >
        <div className="flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24] flex items-center justify-center shadow-lg shadow-orange-200">
            {uploading ? <Loader2 className="w-7 h-7 text-white animate-spin" /> : <Upload className="w-7 h-7 text-white" />}
          </div>
          <p className="font-semibold text-stone-800">Arrastra tickets aquí o selecciona archivos</p>
          <p className="text-xs text-stone-500">JPG · PNG · WEBP · HEIC · PDF — Múltiples a la vez</p>
          <div className="flex flex-wrap gap-2 justify-center mt-2">
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-stone-900 text-white hover:bg-stone-800"
              data-testid="select-files-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> Seleccionar archivos
            </Button>
            <Button
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploading}
              variant="outline"
              data-testid="take-photo-btn"
            >
              <Camera className="w-4 h-4 mr-2" /> Hacer foto
            </Button>
          </div>
          <input ref={fileInputRef} type="file" multiple accept="image/*,application/pdf" onChange={onFileInputChange} className="hidden" />
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" multiple onChange={onCameraInputChange} className="hidden" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 bg-white rounded-xl border border-stone-100 p-1 w-fit">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition ${
              activeTab === t.key ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-stone-100'
            }`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
            {t.key !== 'all' && counts[t.key] != null && (
              <span className="ml-1.5 text-[10px] opacity-70">{counts[t.key]}</span>
            )}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#FF6B4A] animate-spin" /></div>
      ) : uploads.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center border border-stone-100">
          <Inbox className="w-12 h-12 mx-auto text-stone-300 mb-3" />
          <p className="text-stone-500">No hay archivos en esta vista.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {uploads.map((u) => {
            const meta = STATUS_META[u.status] || STATUS_META.uploaded;
            const Icon = meta.icon;
            const ed = u.extracted_data || {};
            const isImg = (u.file_type || '').startsWith('image/');
            return (
              <div
                key={u.id}
                className="bg-white rounded-2xl border border-stone-100 hover:border-orange-200 hover:shadow-md transition overflow-hidden cursor-pointer"
                onClick={() => openDetail(u)}
                data-testid={`upload-card-${u.id}`}
              >
                <div className="h-32 bg-stone-50 flex items-center justify-center overflow-hidden">
                  {isImg && u.file_url ? (
                    <img src={u.file_url} alt={u.file_name} className="object-cover w-full h-full" />
                  ) : (
                    <FileText className="w-12 h-12 text-stone-300" />
                  )}
                </div>
                <div className="p-3 space-y-2">
                  <div className="flex items-center justify-between gap-1.5 flex-wrap">
                    <Badge className={`${meta.color} border-0`}>
                      <Icon className={`w-3 h-3 mr-1 ${u.status === 'processing' ? 'animate-spin' : ''}`} />
                      {meta.label}
                    </Badge>
                    {u.pdf_total_pages > 1 && (
                      <Badge
                        className="bg-violet-100 text-violet-800 border-0"
                        title={`Página ${(u.page_index ?? 0) + 1} de un PDF con ${u.pdf_total_pages} páginas`}
                        data-testid={`pdf-page-badge-${u.id}`}
                      >
                        Pág. {(u.page_index ?? 0) + 1}/{u.pdf_total_pages}
                      </Badge>
                    )}
                    {u.duplicate_warning && (
                      <Badge className="bg-amber-100 text-amber-800 border-0">
                        <AlertTriangle className="w-3 h-3 mr-1" />Duplicado
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-stone-800 truncate">
                    {ed.supplier_name || u.file_name || 'Sin proveedor'}
                  </p>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-stone-500">{ed.issue_date || '—'}</span>
                    <span className="text-sm font-bold text-stone-900">
                      {ed.total != null ? formatCurrency(ed.total) : '—'}
                    </span>
                  </div>
                  {u.error_message && (
                    <p className="text-[11px] text-rose-600 line-clamp-2">{u.error_message}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detail / Confirm panel */}
      {selected && confirmForm && (
        <div className="fixed inset-0 z-50 flex" data-testid="upload-detail-panel">
          <div className="flex-1 bg-black/40" onClick={closeDetail} />
          <div className="w-full max-w-5xl bg-white shadow-2xl flex flex-col overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b border-stone-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24] flex items-center justify-center">
                  <Eye className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-stone-900">Revisar gasto</h2>
                  <p className="text-xs text-stone-500">{selected.file_name}</p>
                </div>
              </div>
              <div className="flex gap-2">
                {selected.status !== 'confirmed' && (
                  <Button variant="ghost" size="sm" onClick={() => handleReprocess(selected.id)} data-testid="reprocess-btn">
                    <RefreshCw className="w-4 h-4 mr-1" /> Reprocesar
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => handleDelete(selected.id)} className="text-rose-600 hover:bg-rose-50" data-testid="delete-upload-btn">
                  <Trash2 className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={closeDetail} data-testid="close-detail-btn">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Body: split view */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 overflow-hidden">
              {/* Left: image */}
              <div className="bg-stone-50 p-4 overflow-y-auto flex flex-col">
                {(selected.file_type || '').startsWith('image/') ? (
                  <img src={selected.file_url} alt="ticket" className="w-full rounded-lg shadow-sm" />
                ) : (
                  <div className="flex-1 flex items-center justify-center text-stone-400">
                    <a href={selected.file_url} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-2 hover:text-stone-700">
                      <FileText className="w-16 h-16" />
                      <span className="underline text-sm">Abrir PDF</span>
                    </a>
                  </div>
                )}
              </div>

              {/* Right: form */}
              <div className="p-5 overflow-y-auto space-y-4">
                {selected.status === 'failed' && (
                  <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 flex gap-2 text-sm text-rose-800">
                    <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Extracción fallida</p>
                      <p className="text-xs">{selected.error_message}</p>
                      <p className="text-xs mt-1">Puedes rellenar los datos manualmente y confirmar igualmente.</p>
                    </div>
                  </div>
                )}
                {confirmForm._duplicate && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2 text-sm text-amber-900" data-testid="duplicate-warning">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Posible duplicado detectado</p>
                      <p className="text-xs">Ya existe un gasto similar de este proveedor por {formatCurrency(confirmForm._duplicate.total)}. Revisa antes de confirmar.</p>
                    </div>
                  </div>
                )}
                {confirmForm._match && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex gap-2 text-sm text-blue-900" data-testid="provider-match">
                    <Zap className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold">Proveedor reconocido: {confirmForm._match.name}</p>
                      <p className="text-xs">Vinculado automáticamente. Puedes cambiarlo abajo.</p>
                    </div>
                  </div>
                )}

                <FieldGroup
                  label="Proveedor"
                  required
                  conf={confirmForm._conf?.supplier_name}
                  testid="field-vendor"
                >
                  <Input
                    value={confirmForm.vendor}
                    onChange={(e) => setConfirmForm({ ...confirmForm, vendor: e.target.value })}
                    data-testid="vendor-input"
                  />
                </FieldGroup>

                <div className="grid grid-cols-2 gap-3">
                  <FieldGroup label="NIF/CIF" conf={confirmForm._conf?.supplier_tax_id}>
                    <Input
                      value={confirmForm.vendor_tax_id || ''}
                      onChange={(e) => setConfirmForm({ ...confirmForm, vendor_tax_id: e.target.value })}
                      data-testid="vendor-tax-input"
                    />
                  </FieldGroup>
                  <FieldGroup label="Nº Factura/Ticket" conf={confirmForm._conf?.invoice_number}>
                    <Input
                      value={confirmForm.description || ''}
                      onChange={(e) => setConfirmForm({ ...confirmForm, description: e.target.value })}
                      data-testid="invoice-num-input"
                    />
                  </FieldGroup>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <FieldGroup label="Fecha" conf={confirmForm._conf?.issue_date}>
                    <Input
                      type="date"
                      value={confirmForm.expense_date || ''}
                      onChange={(e) => setConfirmForm({ ...confirmForm, expense_date: e.target.value })}
                      data-testid="date-input"
                    />
                  </FieldGroup>
                  <FieldGroup label="Categoría">
                    <Select value={confirmForm.category} onValueChange={(v) => setConfirmForm({ ...confirmForm, category: v })}>
                      <SelectTrigger data-testid="category-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldGroup>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <FieldGroup label="Base" conf={confirmForm._conf?.subtotal}>
                    <Input
                      type="number" step="0.01"
                      value={confirmForm.amount}
                      onChange={(e) => calcTotals('amount', e.target.value)}
                      data-testid="amount-input"
                    />
                  </FieldGroup>
                  <FieldGroup label="IVA %">
                    <Input
                      type="number" step="0.01"
                      value={confirmForm.tax_rate}
                      onChange={(e) => calcTotals('tax_rate', e.target.value)}
                      data-testid="taxrate-input"
                    />
                  </FieldGroup>
                  <FieldGroup label="Total" required conf={confirmForm._conf?.total}>
                    <Input
                      type="number" step="0.01"
                      value={confirmForm.total}
                      onChange={(e) => calcTotals('total', e.target.value)}
                      data-testid="total-input"
                    />
                  </FieldGroup>
                </div>

                <FieldGroup label="Proveedor vinculado">
                  <Select value={confirmForm.provider_id || 'none'} onValueChange={(v) => setConfirmForm({ ...confirmForm, provider_id: v === 'none' ? '' : v, create_provider: false })}>
                    <SelectTrigger data-testid="provider-select"><SelectValue placeholder="Sin vincular" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin vincular</SelectItem>
                      {providers.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!confirmForm.provider_id && confirmForm.vendor && (
                    <label className="flex items-center gap-2 text-xs text-stone-600 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!confirmForm.create_provider}
                        onChange={(e) => setConfirmForm({ ...confirmForm, create_provider: e.target.checked })}
                        data-testid="create-provider-checkbox"
                      />
                      Crear proveedor "{confirmForm.vendor}" automáticamente
                    </label>
                  )}
                </FieldGroup>

                <FieldGroup label="Notas">
                  <Textarea
                    value={confirmForm.notes || ''}
                    onChange={(e) => setConfirmForm({ ...confirmForm, notes: e.target.value })}
                    rows={2}
                    data-testid="notes-input"
                  />
                </FieldGroup>

                <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!confirmForm.is_partial_deductible}
                    onChange={(e) => setConfirmForm({ ...confirmForm, is_partial_deductible: e.target.checked })}
                    data-testid="partial-deductible-checkbox"
                  />
                  <FileWarning className="w-4 h-4 text-amber-600" />
                  Deducible parcial (restauración / combustible)
                </label>

                <div className="border-t border-stone-100 pt-3 mt-2">
                  <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!confirmForm.make_recurring}
                      onChange={(e) => setConfirmForm({ ...confirmForm, make_recurring: e.target.checked })}
                      data-testid="make-recurring-checkbox"
                    />
                    <RefreshCw className="w-4 h-4 text-[#FF6B4A]" />
                    <span>Es un gasto recurrente — añadir a Cash Flow</span>
                  </label>
                  {confirmForm.make_recurring && (
                    <div className="mt-2 ml-7">
                      <Select
                        value={confirmForm.recurring_frequency || 'monthly'}
                        onValueChange={(v) => setConfirmForm({ ...confirmForm, recurring_frequency: v })}
                      >
                        <SelectTrigger className="w-full max-w-[200px]" data-testid="recurring-freq-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Mensual</SelectItem>
                          <SelectItem value="quarterly">Trimestral</SelectItem>
                          <SelectItem value="annual">Anual</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-stone-100 flex justify-between items-center bg-stone-50">
              <div className="text-xs text-stone-500">
                Estado: <span className="font-semibold">{STATUS_META[selected.status]?.label}</span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={closeDetail}>Cancelar</Button>
                {selected.status === 'confirmed' ? (
                  <Button onClick={() => navigate('/expenses')} className="bg-stone-900 text-white">
                    Ver en Gastos <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleConfirm}
                    className="bg-gradient-to-r from-[#FF6B4A] to-[#FBBF24] text-white shadow-lg shadow-orange-200"
                    data-testid="confirm-expense-btn"
                  >
                    <Check className="w-4 h-4 mr-2" /> Confirmar gasto
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FieldGroup = ({ label, required, conf, children, testid }) => {
  const cl = confidenceLabel(conf);
  const ringClass = confidenceColor(conf);
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-xs font-semibold text-stone-700">
          {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
        </Label>
        {cl && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${cl.cls}`} title="Confianza del OCR">
            {cl.tag}
          </span>
        )}
      </div>
      <div className={ringClass ? `rounded-md border ${ringClass}` : ''}>
        {children}
      </div>
    </div>
  );
};

export default ExpenseInboxPage;
